import React, { useState, useEffect, useCallback } from 'react';
import { NotificationManager } from 'react-notifications';
import { useDispatch } from 'react-redux';
import { hideModal, showModal } from 'src/redux/actions/modalActions';
import axios from 'src/api/axios';

const ForgotPasswordForm = ({ onClose, inModal }) => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === 'true';

  // Load reCAPTCHA script
  const loadRecaptcha = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(resolve);
        return;
      }
      
      const existingScript = document.querySelector('script[src*="recaptcha"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          window.grecaptcha.ready(resolve);
        });
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.onload = () => {
        window.grecaptcha.ready(resolve);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }, []);

  useEffect(() => {
    if (recaptchaEnabled) {
      loadRecaptcha().catch((error) => {
        console.error('Error loading reCAPTCHA:', error);
      });
    }
  }, [loadRecaptcha, recaptchaEnabled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);
    
    try {
      let requestData = { email };

      // Execute reCAPTCHA only if enabled
      if (recaptchaEnabled) {
        if (!window.grecaptcha) {
          await loadRecaptcha();
        }
        
        const recaptchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {
          action: 'forgot_password'
        });
        requestData.recaptcha_token = recaptchaToken;
      }

      const response = await axios.post('api/forgot-password', requestData, {
        disableNotification: true // Disable axios interceptor notifications, we handle errors manually
      });
      
      NotificationManager.success(response.data.message || 'Password reset link has been sent to your email!');
      dispatch(hideModal());
    } catch (error) {
      console.error('Forgot password error:', error);
      console.error('Error response:', error.response?.data);
      
      if (!error.response) {
        NotificationManager.error('No server response');
      } else if (error.response?.status === 400 || error.response?.status === 422) {
        // Handle validation errors from backend
        const errorData = error.response.data;
        
        // Laravel validation errors format: { error: { field: ["message1", "message2"] } }
        const validationErrors = errorData.error || errorData.errors || {};
        
        if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors)) {
          // Format errors for display: { field: "first message" }
          const formattedErrors = {};
          
          for (const [key, value] of Object.entries(validationErrors)) {
            if (Array.isArray(value) && value.length > 0) {
              // Take the first error message for each field
              formattedErrors[key] = typeof value[0] === 'string' ? value[0] : String(value[0]);
            } else if (typeof value === 'string') {
              formattedErrors[key] = value;
            }
          }
          
          setErrors(formattedErrors);
          
          // Also show a general notification
          NotificationManager.error('Please correct the errors in the form');
        } else if (errorData.error) {
          NotificationManager.error(String(errorData.error));
        } else if (errorData.message) {
          NotificationManager.error(String(errorData.message));
        } else {
          NotificationManager.error('Failed to send password reset link. Please try again.');
        }
      } else {
        NotificationManager.error(error.message || 'An error occurred');
        if (error.response?.data?.message) {
          NotificationManager.error(error.response.data.message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { value } = e.target;
    setEmail(value);
    
    // Clear error when user starts typing
    if (errors.email) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.email;
        return newErrors;
      });
    }
  };

  return (
    <>
      <div className="w-full mx-auto bg-white md:mt-0 sm:max-w-md lg:max-w-[496px]">
        <div>
          <h2 className="text-[35px] leading-[45px] font-bold text-center">
            Reset Password
          </h2>
          <form className="" onSubmit={handleSubmit}>
            <div>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleChange}
                className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full mt-[30px] p-2.5 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
            
            <div className="flex mt-10 mb-12">
              <button 
                type="submit"
                disabled={isSubmitting}
                className={`bg-[#832EE6] hover:bg-purple-800 text-white px-[45px] mx-auto py-[15px] text-[18px] leading-6 font-bold inline-block rounded-[27px] ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Sending...' : 'Send Reset Instructions'}
              </button>
            </div>
          </form>

          <p className="text-base text-center">
            <span onClick={() => dispatch(showModal('LOGIN_MODAL'))} className="font-bold text-[#832EE6] cursor-pointer">Back to login</span>
          </p>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordForm;